/**
 * Section "Bons de chargement & déchargement" — réutilisable pour Opération Spot
 * ET Transfert (PM Spec LOT 2 — 05/05/2026).
 *
 * Props:
 *  - entityId: string (operation.id ou contractRun.id)
 *  - entityKind: "operation" | "transfer"
 *  - userRole: string (admin, operateur, field_ops…)
 *  - onOpenNoteModal: (type) => void  (callback pour ouvrir LoadingNoteModal)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ExternalSignCodeBlock from './ExternalSignCodeBlock';
import BulkGenerateNotesDrawer from './BulkGenerateNotesDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadingNotesSection = ({ entityId, entityKind = 'operation', onOpenNoteModal, operationWeight }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const endpointBase = entityKind === 'transfer'
    ? `${API}/transfers/${entityId}/loading-notes`
    : `${API}/operations/${entityId}/loading-notes`;

  const fetchStatus = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      // Pour opération spot, on a un endpoint /_status — pour transfert on calcule depuis la liste
      if (entityKind === 'operation') {
        const r = await axios.get(`${endpointBase}/_status`);
        setStatus(r.data);
      } else {
        const r = await axios.get(endpointBase);
        const notes = Array.isArray(r.data) ? r.data : [];
        const findOne = (t) => notes.find(n => n.type === t);
        const summarize = (n) => n ? {
          exists: true,
          signed: (n.signatures || []).length >= 3,
          signaturesCount: (n.signatures || []).length,
          noteNumber: n.noteNumber,
          noteId: n.id,
        } : { exists: false, signed: false, signaturesCount: 0 };
        const loadingNote = summarize(findOne('loading'));
        const unloadingNote = summarize(findOne('unloading'));
        setStatus({
          loadingNote, unloadingNote,
          readyForClosure: loadingNote.signed && unloadingNote.signed,
        });
      }
    } catch (err) {
      console.error('Failed to load notes status', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityKind, endpointBase]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const types = [
    { type: 'loading', label: 'Bon de chargement', status: status?.loadingNote },
    { type: 'unloading', label: 'Bon de déchargement', status: status?.unloadingNote },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm" data-testid="loading-notes-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Bons de chargement & déchargement</h3>
          <p className="text-xs text-gray-500">Documents opérationnels avec signatures embarquées</p>
        </div>
        {entityKind === 'operation' && (
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium"
            data-testid="open-bulk-generate-notes"
          >
            Générer en masse
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {types.map(({ type, label, status: nStatus }) => {
          const exists = nStatus?.exists;
          const signed = nStatus?.signed;
          return (
            <button key={type}
                    onClick={() => onOpenNoteModal?.(type)}
                    data-testid={`open-loading-note-${type}`}
                    className={`text-left p-3 border rounded-lg transition hover:shadow-md
                      ${signed ? 'border-emerald-300 bg-emerald-50'
                              : exists ? 'border-amber-300 bg-amber-50'
                                       : 'border-gray-200 bg-gray-50 hover:bg-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-gray-800">{label}</div>
                  <div className="text-xs mt-0.5">
                    {signed ? (
                      <span className="text-emerald-700">✓ Signé ({nStatus.signaturesCount}/3) — {nStatus.noteNumber}</span>
                    ) : exists ? (
                      <span className="text-amber-700">⏳ {nStatus.signaturesCount}/3 signatures — {nStatus.noteNumber}</span>
                    ) : (
                      <span className="text-gray-500">Non créé — cliquez pour générer</span>
                    )}
                  </div>
                </div>
                <div className={`text-xs font-medium ${signed ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {signed ? 'Complet' : 'À traiter →'}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {status && !status.readyForClosure && (
        <p className="mt-2 text-xs text-amber-700 italic">
          ⚠️ {entityKind === 'transfer' ? 'Ce transfert' : 'Cette opération'} ne peut pas être clôturé{entityKind === 'transfer' ? '' : 'e'} tant que les bons de chargement et de déchargement ne sont pas signés (rappel non bloquant).
        </p>
      )}
      <ExternalSignCodeBlock operationId={entityId} entityKind={entityKind} />
      <BulkGenerateNotesDrawer
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        operationId={entityId}
        operationWeight={operationWeight}
        onGenerated={() => fetchStatus()}
      />
    </div>
  );
};

export default LoadingNotesSection;
